import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { FFFeatureFlagsService } from '@bannerflow/feature-flags';
import { Logger } from '@bannerflow/sentinel-logger';
import { CreativesetDataService } from '@studio/common/creativeSet';
import { BrandService } from '@studio/common/brand';
import { EnvironmentService } from '@studio/common/services/environment.service';
import { catchError, filter, forkJoin, of, switchMap, take, timeout } from 'rxjs';

export const featureFlagShowcaseResolverFn: ResolveFn<unknown> = () => {
    const creativesetDataService = inject(CreativesetDataService);
    const brandService = inject(BrandService);
    const environmentService = inject(EnvironmentService);
    const ffFeatureFlagsService = inject(FFFeatureFlagsService);

    if (!environmentService.appEnvironment.featureFlags.enabled) {
        return of(undefined);
    }

    const brandData$ = brandService.brand$.pipe(take(1));
    const creativeSetData$ = creativesetDataService.creativeset$.pipe(filter(Boolean), take(1));

    const update$ = ffFeatureFlagsService.update$.pipe(
        take(1),
        timeout(3000),
        catchError(error => {
            const logMessage =
                error instanceof Error && error.name === 'TimeoutError'
                    ? 'Feature Flag Service took to long to be ready.'
                    : error;

            Logger.warn(logMessage);
            return of(undefined);
        })
    );

    return forkJoin([brandData$, creativeSetData$]).pipe(
        switchMap(([brand, creativeSet]) => {
            const newContextProperties: Record<string, string> = {
                accountId: brand.accountId,
                accountSlug: brand.accountSlug,
                brandId: brand.id,
                brandSlug: brand.slug
            };

            if (creativeSet.id) {
                newContextProperties['creativeSetId'] = creativeSet.id;
            }
            const newContext = {
                properties: newContextProperties
            };
            ffFeatureFlagsService.updateContext(newContext);
            ffFeatureFlagsService.start();
            return update$;
        })
    );
};
